import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, AlertCircle, CheckCircle2, XCircle, X,
  Search, Bug, Wrench, CheckCircle, Circle, ExternalLink, GitPullRequest,
  Smartphone, ShieldCheck, Rocket,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { useAuthStore } from '@/stores/auth';
import {
  isSupportDeskRole, platformBadge, STATUS_STYLES, statusLabel,
  canApprove, isLockedDeploying, isTerminal,
  type BugTestDetail, type AgentAction,
} from './bugTestConstants';

// Bug Test detail — agent timeline, verification evidence, deploy-readiness
// checklist, and the human approve/reject gate with the deploy lock (409 when
// the run is no longer in awaiting_approval|failed). Polls every 5s while the
// run is non-terminal (mirrors the ServiceTicketsPage setInterval idiom).

function phaseIcon(phase: string) {
  const p = (phase || '').toLowerCase();
  if (p.includes('analy')) return <Search size={13} />;
  if (p.includes('repro')) return <Bug size={13} />;
  if (p.includes('fix')) return <Wrench size={13} />;
  if (p.includes('verif')) return <CheckCircle size={13} />;
  return <Circle size={13} />;
}

export function BugTestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [run, setRun] = useState<BugTestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const statusRef = useRef<string | null>(null);

  const fetch = useCallback(async (silent = false) => {
    if (!id) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await api.get(`/support/bug-fix-runs/${id}`);
      const r: BugTestDetail = res.data.data?.run ?? res.data.data ?? null;
      setRun(r);
      statusRef.current = r?.status ?? null;
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to load bug-fix run';
      if (!silent) setError(msg);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  // Poll while non-terminal; stop on unmount and once terminal.
  useEffect(() => {
    const interval = setInterval(() => {
      const s = statusRef.current;
      if (s && !isTerminal(s)) fetch(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [fetch]);

  const approve = async () => {
    if (!id) return;
    setActing(true);
    setActionError(null);
    try {
      const res = await api.post(`/support/bug-fix-runs/${id}/approve`);
      const updated: BugTestDetail = res.data.data?.run ?? null;
      if (updated) { setRun(updated); statusRef.current = updated.status; }
      else fetch();
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || (status === 409 ? 'This run is already in progress' : 'Could not approve');
      setActionError(msg);
      if (status === 409) fetch(true);
    } finally {
      setActing(false);
    }
  };

  const reject = async () => {
    if (!id || !rejectReason.trim()) return;
    setActing(true);
    setActionError(null);
    try {
      const res = await api.post(`/support/bug-fix-runs/${id}/reject`, { reason: rejectReason.trim() });
      const updated: BugTestDetail = res.data.data?.run ?? null;
      if (updated) { setRun(updated); statusRef.current = updated.status; }
      else fetch();
      setShowReject(false);
      setRejectReason('');
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || (status === 409 ? 'This run is already in progress' : 'Could not reject');
      setActionError(msg);
      if (status === 409) fetch(true);
    } finally {
      setActing(false);
    }
  };

  // Desk gate — reporters land on /support/my.
  if (!user) return null;
  if (!isSupportDeskRole(user.role?.name)) {
    return <Navigate to="/support/my" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <AlertCircle size={18} className="mb-2" />
        {error || 'Bug-fix run not found'}
      </div>
    );
  }

  const ticket = run.supportTicket;
  const badge = platformBadge(run.platform);
  const actions: AgentAction[] = Array.isArray(run.agentActions) ? run.agentActions : [];
  const verify = run.verifyResults;
  const checks = run.deployChecks;
  const deploy = run.deploy;

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/support/bug-tests')}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            {ticket?.number && <span className="font-mono">{ticket.number}</span>}
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${badge.className}`}>
              <span>{badge.emoji}</span> {badge.label}
            </span>
            {run.confidence && (
              <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                {run.confidence.replace(/_/g, ' ')}
              </span>
            )}
            {ticket?.module && (
              <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                {ticket.module}
              </span>
            )}
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {ticket?.subject || 'Bug-fix run'}
          </h1>
          {ticket?.createdById && (
            <div className="mt-0.5 text-xs text-gray-500">Reporter: {ticket.createdById}</div>
          )}
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[run.status] || STATUS_STYLES.queued}`}>
          {statusLabel(run.status)}
        </span>
      </div>

      <div className="space-y-4">
        {/* Agent action timeline */}
        <Panel title="Agent activity">
          {actions.length === 0 ? (
            <div className="text-xs text-gray-400">No activity recorded yet.</div>
          ) : (
            <ol className="space-y-2">
              {actions.map((a, i) => (
                <li key={i} className="flex items-start gap-2 border-b border-gray-100 pb-2 last:border-0 last:pb-0 dark:border-gray-800">
                  <span className="mt-0.5 text-gray-400">{phaseIcon(a.phase)}</span>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{a.title}</span>
                      <span className="text-[11px] uppercase tracking-wide text-gray-400">{a.phase}</span>
                    </div>
                    {a.detail && <div className="whitespace-pre-wrap text-xs text-gray-600 dark:text-gray-400">{a.detail}</div>}
                    {a.ts && <div className="text-[11px] text-gray-400">{formatDate(a.ts)}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Panel>

        {/* Root cause */}
        {run.rootCause && (
          <Panel title="Root cause">
            <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{run.rootCause}</p>
          </Panel>
        )}

        {/* Verify block — platform-aware */}
        <Panel title="How to verify">
          {run.platform === 'mobile' ? (
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <div className="flex items-start gap-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-purple-800 dark:border-purple-900 dark:bg-purple-950 dark:text-purple-200">
                <Smartphone size={16} className="mt-0.5" />
                <div>
                  Test build{' '}
                  {deploy?.otaVersion && <strong>v{deploy.otaVersion}</strong>}{' '}
                  pushed to{' '}
                  {deploy?.testDevice ? <strong>{deploy.testDevice}</strong> : 'the test device'}
                  {deploy?.otaChannel ? ` via ${deploy.otaChannel}` : ' via OTA'} — open AURA on the test
                  device, then:
                </div>
              </div>
              {run.reproSteps && (
                <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{run.reproSteps}</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {run.reproSteps && (
                <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{run.reproSteps}</p>
              )}
              {run.devVerifyUrl && (
                <a
                  href={run.devVerifyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)]"
                >
                  Open dev server <ExternalLink size={14} />
                </a>
              )}
            </div>
          )}
        </Panel>

        {/* Verification evidence */}
        {verify && (
          <Panel title="Verification evidence">
            <div className="flex flex-wrap gap-2 text-xs">
              {verify.analyzeDelta !== undefined && (
                <Chip ok={verify.analyzeDelta === 0}>analyze delta {verify.analyzeDelta}</Chip>
              )}
              {verify.smokeTotal !== undefined && (
                <Chip ok={verify.smokePassed === verify.smokeTotal}>
                  smoke {verify.smokePassed ?? 0}/{verify.smokeTotal}
                </Chip>
              )}
              {verify.typecheckBaseline !== undefined && (
                <Chip ok={verify.typecheckBaseline === 0}>typecheck baseline {verify.typecheckBaseline}</Chip>
              )}
              {(verify.newTests ?? []).map((t, i) => (
                <span key={i} className="inline-flex rounded-full bg-teal-100 px-2 py-0.5 font-medium text-teal-700 dark:bg-teal-950 dark:text-teal-300">
                  {t}
                </span>
              ))}
            </div>
            {verify.notes && <p className="mt-2 whitespace-pre-wrap text-xs text-gray-600 dark:text-gray-400">{verify.notes}</p>}
          </Panel>
        )}

        {/* Deploy-readiness checklist */}
        {checks && (
          <Panel title="Deploy readiness">
            <div className="space-y-1.5">
              <CheckRow ok={!!checks.backupPath} label="Backup created" note={checks.backupPath} />
              <CheckRow ok={!!checks.buildClean} label="Build clean" />
              <CheckRow ok={!!checks.health200} label="Health 200" />
              <CheckRow ok={!!checks.routesMounted} label="Routes mounted" />
              <CheckRow ok={!checks.schemaChange} warn={!!checks.schemaChange}
                label={checks.schemaChange ? 'Schema change — needs migration' : 'No schema change'} />
            </div>
          </Panel>
        )}

        {/* PR link */}
        {run.prUrl && (
          <Panel title="Pull request">
            <a
              href={run.prUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[var(--ink-0)] hover:underline"
            >
              <GitPullRequest size={14} /> {run.prUrl}
            </a>
          </Panel>
        )}

        {/* Action bar */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          {actionError && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              <AlertCircle size={14} /> {actionError}
            </div>
          )}

          {canApprove(run.status) ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={approve}
                disabled={acting}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {acting ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
                Approve &amp; Deploy
              </button>
              <button
                onClick={() => { setShowReject(true); setActionError(null); }}
                disabled={acting}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <XCircle size={14} /> Reject
              </button>
            </div>
          ) : isLockedDeploying(run.status) ? (
            <button
              disabled
              className="flex cursor-not-allowed items-center gap-1.5 rounded-lg bg-teal-600/80 px-4 py-2 text-sm font-semibold text-white"
            >
              <Loader2 size={14} className="animate-spin" /> Deploying…
            </button>
          ) : run.status === 'deployed' ? (
            <div className="flex items-center gap-2 text-sm font-semibold text-green-700 dark:text-green-400">
              <ShieldCheck size={16} /> Shipped ✓{ticket?.number ? ` · ${ticket.number} resolved` : ''}
            </div>
          ) : run.status === 'rejected' ? (
            <div className="flex items-start gap-2 text-sm font-semibold text-red-700 dark:text-red-400">
              <XCircle size={16} className="mt-0.5" /> Rejected{run.rejectionReason ? ` — ${run.rejectionReason}` : ''}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Run in progress — no action available yet ({statusLabel(run.status)}).</div>
          )}
        </div>
      </div>

      {/* Reject modal */}
      {showReject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowReject(false)}
        >
          <div
            className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl dark:bg-gray-900"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reject this fix</h2>
              <button onClick={() => setShowReject(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Reason *</label>
              <textarea
                rows={3}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Why is this fix being rejected? (reopens the linked ticket)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
              {actionError && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  <AlertCircle size={14} /> {actionError}
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowReject(false)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={reject}
                disabled={acting || !rejectReason.trim()}
                className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {acting && <Loader2 size={14} className="animate-spin" />}
                Reject fix
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
      {children}
    </section>
  );
}

function Chip({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
      ok
        ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
    }`}>
      {ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />} {children}
    </span>
  );
}

function CheckRow({ ok, label, note, warn }: { ok: boolean; label: string; note?: string; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={`inline-flex items-center gap-1.5 ${
        warn ? 'text-amber-700 dark:text-amber-400' : ok ? 'text-gray-700 dark:text-gray-300' : 'text-red-700 dark:text-red-400'
      }`}>
        {ok && !warn ? <CheckCircle2 size={14} className="text-green-600" />
          : warn ? <AlertCircle size={14} className="text-amber-500" />
          : <XCircle size={14} className="text-red-500" />}
        {label}
      </span>
      {note && <span className="max-w-[55%] truncate font-mono text-xs text-gray-400" title={note}>{note}</span>}
    </div>
  );
}
